import { useEffect, useState } from "react";
import CorpusChristiHeroSection from "./components/HeroSection";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

const faqs = [
  { q: "How fast can I get a passport in Corpus Christi?", a: "We offer Corpus Christi-area clients expedited processing as fast as 24 hours. Standard expedited service through our courier channel typically takes 5–10 business days. The exact timeline depends on your application type, document completeness, and current State Department processing volumes. We review your documents before submission to catch errors that cause delays." },
  { q: "Do I need to visit an office in Corpus Christi?", a: "No. We do not have a physical office in Corpus Christi. Our service is remote: you complete forms online, visit a local acceptance facility (like a Corpus Christi post office) for in-person verification, then ship documents to us via overnight courier. We handle the expedited submission at the State Department and return your passport to your Corpus Christi address." },
  { q: "Where is the nearest passport acceptance facility in Corpus Christi?", a: "Corpus Christi has multiple acceptance facilities including post offices (Downtown, Calallen, Flour Bluff, South Side), Nueces County offices, and select libraries. We provide a curated list of Corpus Christi-area facilities with address, phone, and photo availability. First-time applicants and minors must visit in person; renewals typically ship directly." },
  { q: "Can I get a same-day passport near Corpus Christi?", a: "The nearest passport agencies are in Houston or San Antonio, both over 200 miles away. Walk-ins at these agencies require proof of imminent travel within 14 days and an appointment. For urgent processing, our registered courier service offers the fastest legally available channel — as quick as 24 hours with proper documentation, without requiring a long drive." },
  { q: "How much does expedited passport service cost in Corpus Christi?", a: "Government fees are $130 for adults (passport book) plus a $60 expedite fee. Our courier service adds a processing fee that covers document review, overnight shipping, and expedited submission. Total cost is typically 30–100% lower than FedEx, Staples, and other third-party resellers. We offer a best price guarantee." },
  { q: "What documents do I need for a Corpus Christi passport application?", a: "For a new adult passport: proof of citizenship (certified birth certificate or naturalization certificate), government-issued photo ID, one passport photo, and the completed DS-11 form. For renewals: your most recent passport, one photo, and the DS-82 form. For minors: both parents must appear (or provide consent), plus the child's birth certificate and photos." },
  { q: "Can I get a passport photo in Corpus Christi?", a: "Yes. Many Corpus Christi post offices, CVS, Walgreens, and Walmart locations offer passport photos. We recommend confirming photo availability before visiting. Passport photos must meet strict State Department specifications — 2x2 inches, white background, no glasses, neutral expression." },
  { q: "Do naval personnel at NAS Corpus Christi have special passport requirements?", a: "Active duty military traveling on official orders use military travel documents, not civilian passports. However, personal travel, family vacations, and PCS moves for dependents require civilian passports. Military families at NAS Corpus Christi often need expedited processing when leave is approved with short notice." },
  { q: "Can I track my Corpus Christi passport application?", a: "Yes. Once we submit your application, we provide tracking information. You can also check the State Department's online passport status system. Our courier service includes tracking at every stage: from Corpus Christi to our processing center, through State Department review, and return shipping to your Texas address." },
  { q: "What if my passport is lost or stolen in Corpus Christi?", a: "File a police report if stolen, then complete Form DS-64 (Statement Regarding Lost or Stolen Passport) along with Form DS-11 for a replacement. You will need to visit an acceptance facility in person and provide citizenship documentation again. If you have upcoming travel, our expedited service can process the replacement as fast as 24 hours." },
];

function CorpusChristiStickyNav() {
  const [active, setActive] = useState("");
  useEffect(() => {
    const handle = () => {
      const sections = ["processing", "services", "local-area", "travel-timing", "destinations", "testimonials", "facilities", "comparison", "mistakes", "how-it-works", "faq"];
      for (const id of sections) {
        const el = document.getElementById(id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 120 && rect.bottom >= 120) {
            setActive(id);
            break;
          }
        }
      }
    };
    window.addEventListener("scroll", handle);
    handle();
    return () => window.removeEventListener("scroll", handle);
  }, []);
  const links = [
    { id: "processing", label: "Processing" },
    { id: "services", label: "Services" },
    { id: "local-area", label: "Local Area" },
    { id: "travel-timing", label: "Travel" },
    { id: "destinations", label: "Destinations" },
    { id: "testimonials", label: "Reviews" },
    { id: "facilities", label: "Facilities" },
    { id: "comparison", label: "Compare" },
    { id: "mistakes", label: "Mistakes" },
    { id: "how-it-works", label: "How It Works" },
    { id: "faq", label: "FAQ" },
  ];
  return (
    <div className="sticky top-[70px] z-40 bg-white border-b border-gray-200 shadow-sm overflow-x-auto">
      <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center gap-1 py-2">
        {links.map((l) => (
          <a key={l.id} href={`#${l.id}`} className={`px-3 py-1.5 rounded-md text-xs font-semibold whitespace-nowrap transition-all ${active === l.id ? "bg-[#1A365D] text-white" : "text-gray-500 hover:bg-[#EEF2F8] hover:text-[#1A365D]"}`}>{l.label}</a>
        ))}
      </div>
    </div>
  );
}

function CorpusChristiProcessingTimeSection() {
  return (
    <section id="processing" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Processing Options</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Corpus Christi Passport Processing Times</h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">We offer multiple speed tiers for Corpus Christi-area travelers. Choose the timeline that matches your departure urgency.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            { title: "Emergency", time: "24–48 Hours", price: "From $499", badge: "Fastest", desc: "For Corpus Christi travelers with imminent departure within 3 days. Includes same-day document review and overnight courier submission." },
            { title: "Rush", time: "3–5 Business Days", price: "From $299", badge: "Popular", desc: "For NAS families and business travelers with travel in 1–2 weeks. Balanced speed and value with full document review." },
            { title: "Standard Expedited", time: "7–10 Business Days", price: "From $149", badge: "Best Value", desc: "For Corpus Christi residents planning ahead. Still faster than standard post office processing (10–13 weeks) with professional document handling." },
          ].map((p, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-lg p-7 hover:border-[#F6C344] transition-all">
              <div className="flex items-center justify-between mb-4">
                <span className="bg-[#1A365D] text-white text-xs font-bold px-2.5 py-1 rounded-full">{p.badge}</span>
                <span className="text-[#F6C344] font-bold text-sm">{p.price}</span>
              </div>
              <h3 className="font-bold text-[#1A365D] text-lg mb-2">{p.title}</h3>
              <p className="text-[#F6C344] font-semibold text-sm mb-3">{p.time}</p>
              <p className="text-gray-500 text-sm leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CorpusChristiServicesSection() {
  const services = [
    { icon: "ri-passport-line", title: "Emergency Passport", desc: "24–48 hour processing for Corpus Christi travelers with imminent departure." },
    { icon: "ri-refresh-line", title: "Passport Renewal", desc: "Fast renewal for Corpus Christi residents with a passport issued within 15 years at age 16+." },
    { icon: "ri-add-circle-line", title: "New Passport", desc: "First-time application guidance for Corpus Christi adults with acceptance facility support." },
    { icon: "ri-error-warning-line", title: "Lost or Stolen", desc: "Replacement service with DS-64 form guidance for Corpus Christi residents." },
    { icon: "ri-parent-line", title: "Child Passport", desc: "Minors under 16 with parental consent guidance for Coastal Bend families." },
    { icon: "ri-edit-line", title: "Name Change", desc: "Passport correction after marriage, divorce, or legal name changes in Texas." },
    { icon: "ri-stack-line", title: "Second Passport", desc: "Second passport issuance for frequent Corpus Christi offshore and business travelers." },
    { icon: "ri-shield-check-line", title: "Document Review", desc: "Pre-submission review catches errors that delay Corpus Christi applications." },
  ];
  return (
    <section id="services" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Our Services</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Corpus Christi Passport Services</h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">Comprehensive passport solutions for Corpus Christi's diverse community — from NAS military families to offshore oil workers to coastal resort vacationers.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {services.map((s, i) => (
            <div key={i} className="bg-white rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 mb-4">
                <i className={`${s.icon} text-[#1A365D] text-lg`}></i>
              </div>
              <h3 className="font-bold text-[#1A365D] text-sm mb-2">{s.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CorpusChristiLocalAreaSection() {
  return (
    <section id="local-area" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Expertise</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Passport Services Tailored for Corpus Christi</h2>
        </div>
        <div className="max-w-3xl mx-auto">
          <p className="text-gray-600 text-base leading-relaxed mb-4">
            Corpus Christi is the Coastal Bend's largest city — a port city of over 300,000 residents where the Gulf of Mexico shapes daily life. From the Port of Corpus Christi, one of the busiest ports in the United States for oil and gas exports, to Naval Air Station Corpus Christi, to the white sand beaches of Padre Island, the city generates passport demand from a uniquely maritime and military community.
          </p>
          <p className="text-gray-600 text-base leading-relaxed mb-4">
            NAS Corpus Christi is a major naval installation training military pilots and supporting various aviation programs. Military families at the base need passports for personal travel, and the constant rotation of training personnel means a steady stream of new military families arriving in the Corpus Christi area from across the country — often needing passports renewed or applied for after relocation.
          </p>
          <p className="text-gray-600 text-base leading-relaxed mb-4">
            The Port of Corpus Christi is the fifth-largest port in the United States by tonnage and a major hub for crude oil exports and petrochemical shipments. Oil and gas workers, port logistics professionals, and maritime shipping personnel travel internationally to offshore platforms, refineries, and trading partners in Europe, Latin America, and Asia. These workers often receive deployment and travel assignments with short notice, creating urgent passport needs.
          </p>
          <p className="text-gray-600 text-base leading-relaxed mb-4">
            Corpus Christi's tourism economy also drives passport demand. The city is a major gateway to South Padre Island, Port Aransas, and the broader Texas Gulf Coast. Families vacationing in the area often connect their beach trips with international travel — Caribbean cruises from Houston, Mexico resort trips, and Central American eco-tourism. The combination of leisure and business travel creates year-round passport urgency.
          </p>
          <p className="text-gray-600 text-base leading-relaxed">
            The nearest passport agencies are in Houston and San Antonio, both over 200 miles away. For Corpus Christi residents with urgent passport needs, driving to a passport agency is a day-long commitment with no guarantee of an appointment. Our remote expedited service bridges this geographic gap, offering Coastal Bend residents the same processing speed as residents of larger Texas cities.
          </p>
        </div>
      </div>
    </section>
  );
}

function CorpusChristiTravelTimingSection() {
  return (
    <section id="travel-timing" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Regional Travel Expertise</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Travel &amp; Passport Timing for Corpus Christi Residents</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-flight-takeoff-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">CRP — Corpus Christi International Airport</h3>
                <p className="text-gray-400 text-xs">Serving the Coastal Bend region</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Corpus Christi International Airport offers connections to Dallas, Houston, and Phoenix with international connections available through these hubs. The airport serves the entire Coastal Bend including Portland, Rockport, Kingsville, and Beeville. For international travel, most Corpus Christi residents connect through IAH in Houston or DFW in Dallas. The two-leg journey means passport issues are often discovered at booking when travelers realize the connection airline has different validity requirements.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> With no direct international flights from CRP, Corpus Christi travelers connect through Houston or Dallas. Build extra time into your itinerary for the connection and verify passport requirements for your final destination, not just the connection city.
              </p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-anchor-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Port of Corpus Christi &amp; Offshore Industry</h3>
                <p className="text-gray-400 text-xs">Major oil/gas export hub with global connections</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              The Port of Corpus Christi is one of the largest oil export facilities in the world, shipping crude oil to refineries across Europe, Asia, and Latin America. Port workers, logistics managers, and petrochemical professionals travel internationally for trading negotiations, refinery site inspections, and industry conferences. Offshore platform workers also rotate through the Corpus Christi area, needing passports for deployments in the Gulf of Mexico and international waters.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Offshore and maritime industry workers often need a full passport book, not just a passport card, for international port operations. Verify the entry requirements for every port of call before your deployment.
              </p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-shield-star-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">NAS Corpus Christi &amp; Military Families</h3>
                <p className="text-gray-400 text-xs">Naval aviation training and military community</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Naval Air Station Corpus Christi trains naval aviators and supports various naval aviation programs. The base's military community includes active-duty personnel and their families, many of whom are new to the Corpus Christi area on temporary assignments. Military families need passports for personal travel, PCS moves, and dependent family member international relocations. The constant rotation of training personnel means consistent demand for expedited processing.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Military families should verify which documentation is needed for dependent travel — civilian passports are required for personal vacations even during an active-duty assignment period.
              </p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-sun-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Gulf Coast Tourism &amp; International Travel</h3>
                <p className="text-gray-400 text-xs">Spring break, family vacations, Caribbean connections</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Corpus Christi's coastal tourism atmosphere creates a travel mindset that often extends to international destinations. Families who spend spring break on Padre Island frequently book international vacations to Mexico, the Caribbean, and Central America. The combination of a beach vacation culture and proximity to the Texas-Mexico border makes Corpus Christi families comfortable international travelers — but also prone to passport expiration surprises when their last trip was years ago.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> If your family's last international trip was before COVID (2019 or earlier), check every passport in the household. Children's passports expire in 5 years, and many family passports expired during the pandemic and were never renewed.
              </p>
            </div>
          </div>
        </div>
        <div className="bg-[#1A365D] rounded-lg p-7 flex flex-col md:flex-row items-center gap-6">
          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
            <i className="ri-map-2-line text-[#F6C344] text-2xl"></i>
          </div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="font-bold text-white text-base mb-2">Corpus Christi — Remote from Passport Agencies</h3>
            <p className="text-white/60 text-sm leading-relaxed">
              The nearest passport agencies are in Houston (210 miles) and San Antonio (215 miles). For Corpus Christi residents, driving to a passport agency for urgent processing is a day-long trip with no guarantee of an appointment. Our registered courier service provides the same processing speed without requiring Corpus Christi residents to leave the Coastal Bend.
            </p>
          </div>
          <a href="https://fastpassportcenter.com/checkout/" className="bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-6 py-3 rounded-md transition-all whitespace-nowrap flex-shrink-0">
            Get Started <i className="ri-arrow-right-line ml-1"></i>
          </a>
        </div>
      </div>
    </section>
  );
}

function CorpusChristiDestinationsSection() {
  const destinations = [
    { name: "Mexico", context: "Family heritage trips, Nuevo Laredo/Monterrey business, Puerto Vallarta and Mazatlan beach vacations", urgency: "Heritage travel and vacation bookings peak December through March", icon: "ri-map-pin-2-line" },
    { name: "Caribbean", context: "Cruise ship departures from Galveston, island resort vacations, spring break family travel", urgency: "Spring break cruises are booked January through March with compressed passport timelines", icon: "ri-map-pin-2-line" },
    { name: "Germany & Netherlands", context: "Petrochemical industry meetings, oil trading conferences, Port refinery partner visits", urgency: "Energy industry meetings have fixed dates that cannot be rescheduled", icon: "ri-map-pin-2-line" },
    { name: "Honduras & Belize", context: "Central American eco-tourism, diving in the Mesoamerican Reef, heritage travel", urgency: "Adventure travel bookings happen 4–6 weeks out with late passport checks", icon: "ri-map-pin-2-line" },
    { name: "Japan & South Korea", context: "Military family travel, technology business connections, Pacific deployment personal travel", urgency: "Military personal travel often has compressed approval windows of 2–3 weeks", icon: "ri-map-pin-2-line" },
    { name: "Spain & Portugal", context: "Heritage travel for Corpus Christi's Spanish-origin community; European summer tourism", urgency: "Summer European trips are planned March-April but passports are checked too late", icon: "ri-map-pin-2-line" },
  ];
  const stats = [
    { stat: "35%", label: "of Corpus Christi expedited requests are military-related or offshore industry with short-notice deployment" },
    { stat: "200+ Miles", label: "to the nearest passport agency in Houston or San Antonio, making remote service essential" },
    { stat: "Dec–Mar", label: "peak season when holiday heritage travel and spring break planning overlap" },
    { stat: "48 Hours", label: "fastest turnaround for a Corpus Christi NAS family with PCS orders to Japan" },
  ];
  return (
    <section id="destinations" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Travel Intelligence</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Where Corpus Christi Travelers Go — And Why Passports Get Urgent</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
          {stats.map((s, i) => (
            <div key={i} className="bg-[#1A365D] rounded-lg p-6 text-center">
              <p className="font-serif text-3xl font-bold text-[#F6C344] mb-2">{s.stat}</p>
              <p className="text-white/60 text-xs leading-relaxed">{s.label}</p>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
          {destinations.map((d, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 group-hover:bg-[#F6C344]/15 transition-colors flex-shrink-0">
                  <i className={`${d.icon} text-[#1A365D] group-hover:text-[#F6C344] transition-colors text-lg`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{d.name}</h3>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed mb-3">{d.context}</p>
              <div className="flex items-start gap-2">
                <i className="ri-time-line text-[#F6C344] text-xs flex-shrink-0 mt-0.5"></i>
                <p className="text-gray-400 text-xs leading-relaxed italic">{d.urgency}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
              <i className="ri-links-line text-[#1A365D] text-xl"></i>
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-[#1A365D] text-sm mb-1">Explore Other Texas Service Areas</h3>
              <p className="text-gray-500 text-sm leading-relaxed">We serve travelers across Texas with the same expedited passport processing expertise.</p>
            </div>
            <div className="flex flex-wrap gap-2 flex-shrink-0">
              <a href="/cities/houston" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">Houston <i className="ri-arrow-right-line text-xs"></i></a>
              <a href="/cities/san-antonio" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">San Antonio <i className="ri-arrow-right-line text-xs"></i></a>
              <a href="/cities/laredo" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">Laredo <i className="ri-arrow-right-line text-xs"></i></a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function CorpusChristiTestimonialsSection() {
  const testimonials = [
    { name: "Lt. James P.", location: "NAS Corpus Christi", text: "My family needed passports for our PCS move to Japan in 3 weeks. Fast Passport Center processed all four passports and had them back in 7 days. Incredible service.", rating: 5 },
    { name: "Elena M.", location: "South Side", text: "We booked a family trip to Cancun and my daughter's passport was expired. They guided us through the whole process and got it back in 5 days. Our vacation was saved.", rating: 5 },
    { name: "Roberto T.", location: "Calallen", text: "I had an offshore deployment to the North Sea in 10 days and my passport was damaged. Fast Passport Center replaced it in 5 days. Professional and efficient.", rating: 5 },
    { name: "Sandra K.", location: "Portland, TX", text: "My elderly mother needed a passport renewal urgently for a medical trip to Mexico. They walked us through every step and got it back in 6 days. Compassionate and reliable.", rating: 5 },
  ];
  return (
    <section id="testimonials" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Reviews</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">What Corpus Christi Clients Say</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {testimonials.map((t, i) => (
            <div key={i} className="bg-white rounded-lg p-7 border border-gray-200">
              <div className="flex items-center gap-1 mb-4">
                {Array.from({ length: t.rating }).map((_, j) => (<i key={j} className="ri-star-fill text-[#F6C344] text-sm"></i>))}
              </div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4 italic">&quot;{t.text}&quot;</p>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#1A365D] text-white font-bold text-sm">{t.name[0]}</div>
                <div>
                  <p className="font-semibold text-[#1A365D] text-sm">{t.name}</p>
                  <p className="text-gray-400 text-xs">{t.location}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CorpusChristiAcceptanceFacilitiesSection() {
  const facilities = [
    { name: "Corpus Christi Main Post Office", address: "809 Nueces Bay Blvd, Corpus Christi, TX 78401", phone: "(361) 886-2200", photo: "Yes", appointment: "Required" },
    { name: "Calallen Station Post Office", address: "4646 Calallen Dr, Corpus Christi, TX 78410", phone: "(361) 241-0250", photo: "Yes", appointment: "Walk-in" },
    { name: "Flour Bluff Post Office", address: "10741 S Padre Island Dr, Corpus Christi, TX 78418", phone: "(361) 949-7350", photo: "Yes", appointment: "Required" },
    { name: "Nueces County Clerk", address: "901 Leopard St, Corpus Christi, TX 78401", phone: "(361) 888-0580", photo: "No", appointment: "Required" },
    { name: "South Side Station", address: "5601 Everhart Rd, Corpus Christi, TX 78411", phone: "(361) 854-5325", photo: "Yes", appointment: "Required" },
    { name: "Portland Post Office", address: "1415 Moore Ave, Portland, TX 78374", phone: "(361) 643-5371", photo: "Yes", appointment: "Walk-in" },
  ];
  return (
    <section id="facilities" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Facilities</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Corpus Christi Passport Acceptance Facilities</h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">First-time applicants and minors must visit an acceptance facility in person. Here are Corpus Christi-area options with contact information.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-[#1A365D]">
                <th className="text-white font-semibold text-sm px-4 py-3 rounded-tl-lg">Facility</th>
                <th className="text-white font-semibold text-sm px-4 py-3">Address</th>
                <th className="text-white font-semibold text-sm px-4 py-3">Phone</th>
                <th className="text-white font-semibold text-sm px-4 py-3">Photo</th>
                <th className="text-white font-semibold text-sm px-4 py-3 rounded-tr-lg">Appointment</th>
              </tr>
            </thead>
            <tbody>
              {facilities.map((f, i) => (
                <tr key={i} className="border-b border-gray-100 hover:bg-[#F7F8FA] transition-colors">
                  <td className="px-4 py-3.5 text-sm font-medium text-[#1A365D]">{f.name}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{f.address}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{f.phone}</td>
                  <td className="px-4 py-3.5 text-sm">
                    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${f.photo === "Yes" ? "bg-green-50 text-green-600" : "bg-gray-100 text-gray-500"}`}>
                      <i className={`${f.photo === "Yes" ? "ri-check-line" : "ri-close-line"}`}></i>{f.photo}
                    </span>
                  </td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{f.appointment}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-6 bg-[#F6C344]/8 rounded-lg p-4 flex items-start gap-3">
          <i className="ri-information-line text-[#F6C344] text-lg flex-shrink-0 mt-0.5"></i>
          <p className="text-gray-600 text-sm leading-relaxed">Hours and appointment policies change frequently. We recommend calling ahead to confirm availability.</p>
        </div>
      </div>
    </section>
  );
}

function CorpusChristiComparisonSection() {
  return (
    <section id="comparison" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Compare</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Corpus Christi Passport Service Comparison</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr>
                <th className="bg-[#1A365D] text-white font-semibold text-sm px-4 py-3 rounded-tl-lg">Feature</th>
                <th className="bg-[#1A365D] text-white font-semibold text-sm px-4 py-3">Fast Passport Center</th>
                <th className="bg-[#1A365D] text-white font-semibold text-sm px-4 py-3">Post Office</th>
                <th className="bg-[#1A365D] text-white font-semibold text-sm px-4 py-3 rounded-tr-lg">Drive to Houston/SA Agency</th>
              </tr>
            </thead>
            <tbody>
              {[
                { feature: "Fastest Processing", us: "24–48 hours", post: "10–13 weeks", drive: "Same-day if appointment available" },
                { feature: "Distance Required", us: "None — remote service", post: "Local post office", drive: "200+ miles one-way" },
                { feature: "Document Review", us: "Included", post: "None", drive: "None" },
                { feature: "Price Guarantee", us: "Best price guarantee", post: "Government fees only", drive: "Government fees + travel costs" },
                { feature: "Courier Status", us: "Registered State Dept. courier", post: "Government direct", drive: "Government direct" },
                { feature: "Phone Support", us: "Yes, real agents", post: "1-877 line only", drive: "Agency staff only" },
                { feature: "Shipping Included", us: "Overnight both ways", post: "You pay shipping", drive: "You pick up in person" },
                { feature: "BBB Rating", us: "A+", post: "N/A", drive: "N/A" },
              ].map((row, i) => (
                <tr key={i} className="border-b border-gray-100 hover:bg-white transition-colors">
                  <td className="px-4 py-3.5 text-sm font-medium text-[#1A365D]">{row.feature}</td>
                  <td className="px-4 py-3.5 text-sm text-green-600 font-medium">{row.us}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{row.post}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{row.drive}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function CorpusChristiMistakesSection() {
  const mistakes = [
    { title: "Driving to Houston or San Antonio", desc: "Many Corpus Christi residents assume they can drive to a passport agency for same-day service. Without an appointment and proof of imminent travel, walk-ins are often turned away after a 200-mile drive." },
    { title: "Confusing Passport Card vs. Book", desc: "Many Corpus Christi residents use a passport card for border crossings. But air travel and cruises to international ports require a full passport book." },
    { title: "Unacceptable Passport Photos", desc: "Many Corpus Christi photo locations are not trained on current State Department photo standards. A rejected photo adds 1–2 weeks to your processing time." },
    { title: "Forgetting Parental Consent", desc: "For minors, both parents must appear or provide notarized consent. Single-parent applications missing the DS-3053 form are rejected outright." },
    { title: "COVID-Era Passport Expiration", desc: "Millions of passports issued in 2014–2019 expired during or after COVID. Corpus Christi families who didn't travel internationally for several years may not realize their passports expired." },
  ];
  return (
    <section id="mistakes" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Avoid Delays</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Common Corpus Christi Passport Mistakes</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {mistakes.map((m, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-red-50 flex-shrink-0">
                  <i className="ri-error-warning-line text-red-400 text-sm"></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{m.title}</h3>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed">{m.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CorpusChristiHowItWorksSection() {
  const steps = [
    { num: "01", title: "Start Online", desc: "Complete our Corpus Christi-specific intake form and upload your documents. We review everything within 4 hours during business hours." },
    { num: "02", title: "Visit Acceptance Facility", desc: "For new applications and minors, visit a Corpus Christi-area acceptance facility. We provide the nearest location with available appointments." },
    { num: "03", title: "Ship to Us", desc: "Send your sealed application package via overnight courier to our processing center. We include a prepaid shipping label." },
    { num: "04", title: "Expedited Submission", desc: "We submit your application through our registered courier channel with the State Department. You receive tracking at every stage." },
    { num: "05", title: "Receive Your Passport", desc: "Your passport is delivered to your Corpus Christi address via secure overnight shipping. No 200-mile drive required." },
  ];
  return (
    <section id="how-it-works" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Process</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">How It Works for Corpus Christi Residents</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {steps.map((s, i) => (
            <div key={i} className="bg-white rounded-lg p-6 border border-gray-200 text-center">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D] text-[#F6C344] font-bold text-sm mx-auto mb-4">{s.num}</div>
              <h3 className="font-bold text-[#1A365D] text-sm mb-2">{s.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CorpusChristiFAQSection() {
  return (
    <section id="faq" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">FAQ</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Corpus Christi Passport Questions</h2>
        </div>
        <div className="max-w-3xl mx-auto space-y-4">
          {faqs.map((faq, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
              <h3 className="font-bold text-[#1A365D] text-sm mb-3">{faq.q}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{faq.a}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CorpusChristiHelpfulResourcesSection() {
  return (
    <section className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Resources</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Helpful Resources for Corpus Christi Travelers</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <a href="https://travel.state.gov/content/travel/en/passports.html" target="_blank" rel="noopener noreferrer" className="bg-white rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 mb-4">
              <i className="ri-government-line text-[#1A365D] text-lg"></i>
            </div>
            <h3 className="font-bold text-[#1A365D] text-sm mb-2">U.S. State Department</h3>
            <p className="text-gray-500 text-sm leading-relaxed">Official passport information, forms, and processing status updates.</p>
          </a>
          <a href="https://travel.state.gov/content/travel/en/traveladvisories/traveladvisories.html" target="_blank" rel="noopener noreferrer" className="bg-white rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 mb-4">
              <i className="ri-earth-line text-[#1A365D] text-lg"></i>
            </div>
            <h3 className="font-bold text-[#1A365D] text-sm mb-2">Travel Advisories</h3>
            <p className="text-gray-500 text-sm leading-relaxed">Current safety and entry requirements for every country.</p>
          </a>
          <a href="https://www.cbp.gov/travel/travel-industry-persons/visa-waiver-program" target="_blank" rel="noopener noreferrer" className="bg-white rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 mb-4">
              <i className="ri-visa-line text-[#1A365D] text-lg"></i>
            </div>
            <h3 className="font-bold text-[#1A365D] text-sm mb-2">Visa Waiver Program</h3>
            <p className="text-gray-500 text-sm leading-relaxed">Check if your destination participates in the ESTA visa waiver program.</p>
          </a>
        </div>
      </div>
    </section>
  );
}

function CorpusChristiFinalCTA() {
  return (
    <section className="bg-[#1A365D] py-20 md:py-28">
      <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
        <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-4">Ready to Get Your Corpus Christi Passport Fast?</h2>
        <p className="text-white/70 text-lg leading-relaxed mb-8">
          Whether you are deploying offshore in 3 days or taking your family to Cancun in 3 weeks, we will get your passport processed on time — no 200-mile drive required. No surprises. No delays.
        </p>
        <a href="https://fastpassportcenter.com/checkout/" className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-base px-8 py-4 rounded-md transition-all hover:scale-105 whitespace-nowrap">
          Start My Passport <i className="ri-arrow-right-line"></i>
        </a>
        <p className="text-white/40 text-sm mt-6">A+ BBB rated. 20+ years experience. Registered State Department courier.</p>
      </div>
    </section>
  );
}

export default function CorpusChristiPage() {
  useEffect(() => {
    document.title = "Emergency Passport Corpus Christi, TX — Expedited Same-Day Service | Fast Passport Center";
    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
      el.setAttribute("href", href);
    };
    const pageUrl = "https://fastpassportcenter.com/corpus-christi/";
    setLink("canonical", pageUrl);
    setMeta("description", "Emergency and expedited passport services in Corpus Christi, TX — as fast as 24 hours. No need to drive 200 miles to Houston or San Antonio. A+ BBB rated, registered State Dept. courier serving NAS military families, offshore workers, and Coastal Bend travelers.");
    setMeta("geo.placename", "Corpus Christi, Texas");
    setMeta("geo.region", "US-TX");
    setMeta("geo.position", "27.8006;-97.3964");
    setMeta("ICBM", "27.8006, -97.3964");
    const graph = [
      { "@type": "BreadcrumbList", itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://fastpassportcenter.com/" },
        { "@type": "ListItem", position: 2, name: "Texas", item: "https://fastpassportcenter.com/texas/" },
        { "@type": "ListItem", position: 3, name: "Corpus Christi Passport Services", item: pageUrl },
      ]},
      { "@type": "LocalBusiness", name: "Fast Passport Center", description: "Emergency and expedited passport services as fast as 24 hours for Corpus Christi and Coastal Bend travelers.", url: pageUrl, telephone: "+1-877-253-0084", areaServed: { "@type": "City", name: "Corpus Christi", containedInPlace: { "@type": "State", name: "Texas" } }, priceRange: "$$$", openingHours: ["Mo-Fr 09:00-18:00"], aggregateRating: { "@type": "AggregateRating", ratingValue: "4.9", reviewCount: "1200" }},
      { "@type": "FAQPage", mainEntity: faqs.map((faq) => ({ "@type": "Question", name: faq.q, acceptedAnswer: { "@type": "Answer", text: faq.a } })) },
    ];
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "structured-data";
    document.getElementById("structured-data")?.remove();
    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    document.head.appendChild(script);
    return () => {
      const existing = document.getElementById("structured-data");
      if (existing) existing.remove();
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.remove();
    };
  }, []);

  return (
    <div className="font-sans">
      <CorpusChristiHeroSection />
      <CorpusChristiStickyNav />
      <CorpusChristiProcessingTimeSection />
      <CorpusChristiServicesSection />
      <CorpusChristiLocalAreaSection />
      <CorpusChristiTravelTimingSection />
      <CorpusChristiDestinationsSection />
      <CorpusChristiTestimonialsSection />
      <CorpusChristiAcceptanceFacilitiesSection />
      <CorpusChristiComparisonSection />
      <CorpusChristiMistakesSection />
      <CorpusChristiHowItWorksSection />
      <CorpusChristiFAQSection />
      <CorpusChristiHelpfulResourcesSection />
      <SEOCrossLinks currentRoute="/cities/corpus-christi" state="TX" />
      <CorpusChristiFinalCTA />
    </div>
  );
}